import type { Maybe } from './globals';
import type { ISettings } from './settings';

// Values match the AI Gate Feed table header keys, so a stored setting maps straight onto a column.
export enum GateFeedColumn {
  Picture = 'images',
  CheckIn = 'checkin',
  ConfirmationNumber = 'confirmationNumber',
  Date = 'date',
  Time = 'time',
  Direction = 'direction',
  VehicleLicensePlate = 'vehicle.licensePlate',
  VehicleNumber = 'vehicle.number',
  VehicleCarrier = 'vehicle.carrier',
  VehicleDotNumber = 'vehicle.dotNumber',
  VehicleClass = 'vehicle.class',
  VehicleFuelType = 'vehicle.fuelType',
  VehicleDamage = 'vehicle.damage',
  ChassisNumber = 'chassis.number',
  ChassisLicensePlate = 'chassis.licensePlate',
  ChassisIsBare = 'chassis.isBare',
  ChassisDamage = 'chassis.damage',
  TrailerLicensePlate = 'trailer.licensePlate',
  TrailerNumber = 'trailer.number',
  TrailerDamage = 'trailer.damage',
  TrailerHasSeal = 'trailer.hasSeal',
  TrailerSealNumber = 'trailer.sealNumber'
}

export enum GateFeedColumnCategory {
  General = 'general',
  Vehicle = 'vehicle',
  Chassis = 'chassis',
  Trailer = 'trailer'
}

export interface IGateFeedColumnMetadata {
  key: GateFeedColumn;
  category: GateFeedColumnCategory;
  isMandatory: boolean;
}

// Order is the display order, both in the settings panel and on the AI Gate Feed table.
export const GATE_FEED_COLUMNS: readonly IGateFeedColumnMetadata[] = [
  {
    key: GateFeedColumn.Picture,
    category: GateFeedColumnCategory.General,
    isMandatory: true
  },
  {
    key: GateFeedColumn.CheckIn,
    category: GateFeedColumnCategory.General,
    isMandatory: true
  },
  {
    key: GateFeedColumn.ConfirmationNumber,
    category: GateFeedColumnCategory.General,
    isMandatory: false
  },
  { key: GateFeedColumn.Date, category: GateFeedColumnCategory.General, isMandatory: true },
  { key: GateFeedColumn.Time, category: GateFeedColumnCategory.General, isMandatory: true },
  {
    key: GateFeedColumn.Direction,
    category: GateFeedColumnCategory.General,
    isMandatory: true
  },
  {
    key: GateFeedColumn.VehicleLicensePlate,
    category: GateFeedColumnCategory.Vehicle,
    isMandatory: false
  },
  {
    key: GateFeedColumn.VehicleNumber,
    category: GateFeedColumnCategory.Vehicle,
    isMandatory: false
  },
  {
    key: GateFeedColumn.VehicleCarrier,
    category: GateFeedColumnCategory.Vehicle,
    isMandatory: false
  },
  {
    key: GateFeedColumn.VehicleDotNumber,
    category: GateFeedColumnCategory.Vehicle,
    isMandatory: false
  },
  {
    key: GateFeedColumn.VehicleClass,
    category: GateFeedColumnCategory.Vehicle,
    isMandatory: false
  },
  {
    key: GateFeedColumn.VehicleFuelType,
    category: GateFeedColumnCategory.Vehicle,
    isMandatory: false
  },
  {
    key: GateFeedColumn.VehicleDamage,
    category: GateFeedColumnCategory.Vehicle,
    isMandatory: false
  },
  {
    key: GateFeedColumn.ChassisNumber,
    category: GateFeedColumnCategory.Chassis,
    isMandatory: false
  },
  {
    key: GateFeedColumn.ChassisLicensePlate,
    category: GateFeedColumnCategory.Chassis,
    isMandatory: false
  },
  {
    key: GateFeedColumn.ChassisIsBare,
    category: GateFeedColumnCategory.Chassis,
    isMandatory: false
  },
  {
    key: GateFeedColumn.ChassisDamage,
    category: GateFeedColumnCategory.Chassis,
    isMandatory: false
  },
  {
    key: GateFeedColumn.TrailerLicensePlate,
    category: GateFeedColumnCategory.Trailer,
    isMandatory: false
  },
  {
    key: GateFeedColumn.TrailerNumber,
    category: GateFeedColumnCategory.Trailer,
    isMandatory: false
  },
  {
    key: GateFeedColumn.TrailerDamage,
    category: GateFeedColumnCategory.Trailer,
    isMandatory: false
  },
  {
    key: GateFeedColumn.TrailerHasSeal,
    category: GateFeedColumnCategory.Trailer,
    isMandatory: false
  },
  {
    key: GateFeedColumn.TrailerSealNumber,
    category: GateFeedColumnCategory.Trailer,
    isMandatory: false
  }
];

export const ALL_GATE_FEED_COLUMNS: readonly GateFeedColumn[] = GATE_FEED_COLUMNS.map(
  column => column.key
);

export const MANDATORY_GATE_FEED_COLUMNS: readonly GateFeedColumn[] = GATE_FEED_COLUMNS.filter(
  column => column.isMandatory
).map(column => column.key);

/**
 * Columns a warehouse has enabled for the AI Gate Feed, in display order.
 *
 * A warehouse that never saved the setting gets every column. Mandatory columns are added back and
 * unknown keys dropped, so a stale or hand-edited value can neither hide a required column nor
 * surface a column that no longer exists.
 */
export function getEnabledGateFeedColumns(settings: Maybe<ISettings>): GateFeedColumn[] {
  const stored = settings?.gateFeedColumns;

  if (!Array.isArray(stored)) {
    return [...ALL_GATE_FEED_COLUMNS];
  }

  return GATE_FEED_COLUMNS.filter(column => column.isMandatory || stored.includes(column.key)).map(
    column => column.key
  );
}
